// Channel between the dashboard's tailoring box and the tailoring engine.
//
// The engine lives in ResumeTailoringClaudeDirect, which stays MOUNTED for the
// whole session (App.tsx PersistentTailoringPage). The dashboard box posts a
// request here; the engine applies the settings and starts the run while the
// user stays on the dashboard. The engine reports its phase back so the box
// can show progress in place.

export type QuickStartTarget = "job-description";
export type QuickStartDepth = "quick" | "deep";
export type QuickStartMode = "Full JD" | "Personalized JD";

export interface TailorQuickStart {
  target: QuickStartTarget;
  jobDescription: string;
  /** Job title, when the job came from the Job Library. */
  jobTitle: string;
  /** The complete job (Job Library only) the resume is SCORED against;
   *  empty for a pasted job, which is scored against itself. */
  completeJd: string;
  resumeText: string;
  resumeName: string;
  /** The resume's own Word file (public URL), when it came from a .docx. */
  originalDocxUrl?: string | null;
  /** Resume design: RDR Blue, or the client's own design cloned from the Word file. */
  design?: 'rdr-blue' | 'original-format';
  documents: string[];
  depth: QuickStartDepth;
  mode: QuickStartMode;
  highlightCount: number;
  generateAnalysis: boolean;
  autoFixFormatting: boolean;
  instructions: string;
  newClientWorksheet: string;
  quantWorksheet: string;
}

export type QuickStartPhase =
  | { phase: "idle" }
  | { phase: "report" }   // Deep: building the Tailoring Report first
  | { phase: "starting" } // settings applied, run being launched
  | { phase: "running" }  // the engine's run is in flight
  | { phase: "error"; message: string };

type RequestListener = (q: TailorQuickStart) => void;
type PhaseListener = (p: QuickStartPhase) => void;

let pending: TailorQuickStart | null = null;
let phase: QuickStartPhase = { phase: "idle" };
const requestListeners = new Set<RequestListener>();
const phaseListeners = new Set<PhaseListener>();

/** Dashboard: ask the engine to start a run with these settings. */
export function requestQuickStart(q: TailorQuickStart): void {
  if (requestListeners.size === 0) {
    pending = q; // engine not mounted yet; it picks this up when it subscribes
    return;
  }
  for (const fn of requestListeners) fn(q);
}

/** Engine: receive requests (and any that arrived before it mounted). */
export function onQuickStartRequest(fn: RequestListener): () => void {
  requestListeners.add(fn);
  if (pending) { const q = pending; pending = null; fn(q); }
  return () => { requestListeners.delete(fn); };
}

/** Engine: report where the run is. */
export function setQuickStartPhase(p: QuickStartPhase): void {
  phase = p;
  for (const fn of phaseListeners) fn(phase);
}

export function getQuickStartPhase(): QuickStartPhase {
  return phase;
}

/** Dashboard: follow the run. */
export function subscribeQuickStartPhase(fn: PhaseListener): () => void {
  phaseListeners.add(fn);
  return () => { phaseListeners.delete(fn); };
}

// ── Live steps ───────────────────────────────────────────────────────────────
// The engine marks each real stage as it starts and finishes, so the dashboard
// can show what is actually happening (stages run in parallel where the
// engine runs them in parallel). Ids in run order; Deep adds "report" first.
export type QuickStartStepId =
  | "report" | "read" | "organize" | "match" | "summary" | "write" | "assemble" | "rules" | "fit";
export type QuickStartStepStatus = "pending" | "active" | "done";

export const QUICK_START_STEPS: { id: QuickStartStepId; active: string; done: string; deepOnly?: boolean }[] = [
  { id: "report", active: "Building your Tailoring Report on the company's biggest problems", done: "Built your Tailoring Report on the company's biggest problems", deepOnly: true },
  { id: "read", active: "Pulling your key facts, contact details and metrics, and planning the summary", done: "Pulled your key facts and planned the summary" },
  { id: "organize", active: "Ranking the job's top responsibilities, requirements and skills", done: "Ranked the job's top responsibilities, requirements and skills" },
  { id: "match", active: "Matching your experience to the job, then writing your headline and skills", done: "Matched your experience to the job and wrote your headline and skills" },
  { id: "summary", active: "Writing your career summary", done: "Wrote your career summary" },
  { id: "write", active: "Writing your career highlights, experience and education", done: "Wrote your career highlights, experience and education" },
  { id: "assemble", active: "Assembling and fact-checking the resume", done: "Assembled and fact-checked the resume" },
  { id: "rules", active: "Checking every rule: lengths, banned words, action verbs", done: "Checked every rule: lengths, banned words, action verbs" },
  { id: "fit", active: "Fitting every line to the page", done: "Fit every line to the page" },
];

export interface QuickStartProgress {
  deep: boolean;
  startedAt: number | null;
  steps: Partial<Record<QuickStartStepId, QuickStartStepStatus>>;
  /** When each step really started and finished (ms epoch). Shown on the card. */
  began: Partial<Record<QuickStartStepId, number>>;
  ended: Partial<Record<QuickStartStepId, number>>;
}

let progress: QuickStartProgress = { deep: false, startedAt: null, steps: {}, began: {}, ended: {} };
const progressListeners = new Set<(p: QuickStartProgress) => void>();
const emitProgress = () => { for (const fn of progressListeners) fn(progress); };

/** Engine: a new quick-start run begins. */
export function resetQuickStartProgress(deep: boolean): void {
  progress = { deep, startedAt: Date.now(), steps: {}, began: {}, ended: {} };
  emitProgress();
}

/** Engine: mark one stage. Done never goes back to active. */
export function setQuickStartStep(id: QuickStartStepId, status: QuickStartStepStatus): void {
  if (progress.steps[id] === "done" && status !== "done") return;
  // Steps overlap (several run at once): every line shows its OWN real state.
  const steps = { ...progress.steps, [id]: status };
  const began = { ...progress.began }, ended = { ...progress.ended };
  if (status === "active" && !began[id]) began[id] = Date.now();
  if (status === "done") { if (!began[id]) began[id] = Date.now(); ended[id] = Date.now(); }
  progress = { ...progress, steps, began, ended };
  emitProgress();
}

/** Engine: the run was stopped; clear the panel. */
export function clearQuickStartProgress(): void {
  progress = { deep: false, startedAt: null, steps: {}, began: {}, ended: {} };
  emitProgress();
}

/** Percent of steps finished (the only honest progress number). */
export function quickStartPercentDone(p: QuickStartProgress = progress): number {
  const list = QUICK_START_STEPS.filter((s) => !s.deepOnly || p.deep);
  const done = list.filter((s) => p.steps[s.id] === "done").length;
  return list.length ? Math.round((done / list.length) * 100) : 0;
}

export function getQuickStartProgress(): QuickStartProgress {
  return progress;
}

export function subscribeQuickStartProgress(fn: (p: QuickStartProgress) => void): () => void {
  progressListeners.add(fn);
  return () => { progressListeners.delete(fn); };
}

// ── Stop (Marcus, Oct 25 2026): the dashboard panel can stop a running tailoring ──
type StopListener = () => void;
const stopListeners = new Set<StopListener>();
/** Dashboard: stop the run NOW. The engine aborts its in-flight call and resets. */
export function requestQuickStartStop(): void {
  for (const fn of stopListeners) fn();
}
/** Engine: receive stop requests. */
export function onQuickStartStop(fn: StopListener): () => void {
  stopListeners.add(fn);
  return () => { stopListeners.delete(fn); };
}
